import { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Pagination, Stack, CircularProgress,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowRight, ContentCopy, Check } from '@mui/icons-material';
import { Card, Select, PageHeader } from 'Components/ui';
import { useTranslation } from 'react-i18next';
import { useAuthTenant } from 'store/auth';
import { getScannerFindingGroups, getScannerFindingsByCheck, getScannerAccounts } from 'services/scanner-service';
import { Main as Container } from 'Components/Main';

// ── Constants ──────────────────────────────────────────────────────────────────
const SEVERITY_DOT = {
  critical: 'var(--vo-sev-critical)', high: 'var(--vo-sev-high)', medium: 'var(--vo-sev-medium)', low: 'var(--vo-sev-low)', informational: 'var(--vo-sev-info)',
};
const SEVERITY_BG = {
  critical: 'var(--vo-danger-10)', high: 'var(--vo-sev-high-12)', medium: 'var(--vo-warning-10)', low: 'var(--vo-sev-low-10)', informational: 'var(--vo-info-8)',
};

// ── Pure HTML badges (avoid MUI Chip theme conflicts) ─────────────────────────
const StatusBadge = ({ status }) => {
  const colors = {
    FAIL: { border: 'var(--vo-danger-15)', color: 'var(--vo-danger)', bg: 'var(--vo-danger-10)' },
    PASS: { border: 'var(--vo-success-20)', color: 'var(--vo-success)', bg: 'var(--vo-success-10)' },
    MUTED: { border: 'var(--vo-line)', color: 'var(--vo-slate)', bg: 'var(--vo-mist)' },
  };
  const c = colors[status] || colors.MUTED;
  return (
    <span style={{
      display: 'inline-block', whiteSpace: 'nowrap',
      padding: '2px 8px', borderRadius: 4,
      border: `1px solid ${c.border}`, backgroundColor: c.bg,
      color: c.color, fontSize: 11, fontWeight: 700, letterSpacing: 0.3,
    }}>
      {status}
    </span>
  );
};

const SeverityBadge = ({ severity }) => {
  const { t } = useTranslation('scanner');
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
      <span style={{
        display: 'inline-block', width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
        backgroundColor: SEVERITY_DOT[severity] || 'var(--vo-steel)',
      }} />
      <span style={{ fontSize: 13, color: 'var(--vo-slate)' }}>
        {t(`scanner:severity.${severity}`) || severity || '—'}
      </span>
    </span>
  );
};

const ResourcesBadge = ({ count }) => {
  const { t } = useTranslation('scanner');
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '3px 10px', borderRadius: 4,
      border: '1px solid var(--vo-line)', backgroundColor: 'var(--vo-white)',
      whiteSpace: 'nowrap',
    }}>
      <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--vo-ink)' }}>{count}</span>
      <span style={{ fontSize: 12, color: 'var(--vo-steel)' }}>
        {count === 1 ? t('scanner:findings.resource_one') : t('scanner:findings.resource_other')}
      </span>
    </span>
  );
};

// ── Helpers ────────────────────────────────────────────────────────────────────
function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('pt-BR', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function failingFor(updatedAt, insertedAt) {
  const since = updatedAt || insertedAt;
  if (!since) return '—';
  const days = Math.floor((Date.now() - new Date(since)) / 86400000);
  if (days <= 0) return '< 1 dia';
  if (days < 30) return `${days} dia${days !== 1 ? 's' : ''}`;
  const months = Math.floor(days / 30);
  const rem = days - months * 30;
  const mLabel = `${months} ${months === 1 ? 'mês' : 'meses'}`;
  return rem > 0 ? `${mLabel} e ${rem} dia${rem !== 1 ? 's' : ''}` : mLabel;
}

// ── Expanded resource rows ─────────────────────────────────────────────────────
const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const handle = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button onClick={handle} title="Copiar ARN" style={{
      background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px',
      color: copied ? 'var(--vo-success)' : 'var(--vo-steel)', display: 'flex', alignItems: 'center',
      transition: 'color 0.15s',
    }}>
      {copied
        ? <Check style={{ fontSize: 11 }} />
        : <ContentCopy style={{ fontSize: 11 }} />
      }
    </button>
  );
};

const ResourceRow = ({ item, accountMap }) => {
  const { t } = useTranslation('scanner');
  const providerId = item._rel?.provider?.data?.id;
  const accountName = accountMap[providerId] || null;
  const accountUid = item.account_uid || providerId || null;
  const rawUid = item.resource_uid || '';
  const resourceName = item.resource_name || rawUid.split('/').filter(Boolean).pop() || rawUid || '—';
  const displayUid = rawUid || (item.account_uid && item.resource_name
    ? `${item.account_uid}/${item.resource_name}`
    : item.account_uid || '—');
  const uidShort = displayUid.length > 50 ? `${displayUid.slice(0, 48)}…` : displayUid;

  return (
    <tr style={{ borderTop: '1px solid var(--vo-line)', backgroundColor: 'var(--vo-white)' }}>
      <td style={{ padding: '10px 16px', whiteSpace: 'nowrap', verticalAlign: 'middle' }}>
        <StatusBadge status={item.status} />
      </td>
      <td style={{ padding: '10px 16px', verticalAlign: 'middle' }}>
        <div style={{ display: 'flex', gap: 32, alignItems: 'flex-start', paddingLeft: 28 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--vo-ink)', lineHeight: 1.3 }}>
              {resourceName}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 3, marginTop: 2 }}>
              <span style={{ fontSize: 11, color: 'var(--vo-steel)', fontFamily: 'monospace' }}
                title={displayUid}>
                {`ARN: ${uidShort}`}
              </span>
              {displayUid !== '—' && <CopyButton text={displayUid} />}
            </div>
          </div>
          {accountName && (
            <div>
              <div style={{ fontSize: 13, color: 'var(--vo-ink-2)', lineHeight: 1.3 }}>{accountName}</div>
              {accountUid && (
                <div style={{ fontSize: 11, color: 'var(--vo-steel)', fontFamily: 'monospace', marginTop: 2 }}>
                  {`UID: ${String(accountUid).slice(0, 18)}${String(accountUid).length > 18 ? '…' : ''}`}
                </div>
              )}
            </div>
          )}
        </div>
      </td>
      <td style={{ padding: '10px 16px', whiteSpace: 'nowrap', verticalAlign: 'middle' }}>
        <SeverityBadge severity={item.severity} />
      </td>
      <td style={{ padding: '10px 16px', whiteSpace: 'nowrap', verticalAlign: 'middle' }}>
        <div style={{ fontSize: 10, color: 'var(--vo-steel)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 }}>{t('scanner:findings.labelService')}</div>
        <div style={{ fontSize: 13, color: 'var(--vo-ink-2)' }}>{item.service_name || '—'}</div>
      </td>
      <td style={{ padding: '10px 16px', whiteSpace: 'nowrap', verticalAlign: 'middle' }}>
        <div style={{ fontSize: 10, color: 'var(--vo-steel)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 }}>{t('scanner:findings.labelRegion')}</div>
        <div style={{ fontSize: 13, color: 'var(--vo-ink-2)' }}>{item.region || '—'}</div>
      </td>
      <td style={{ padding: '10px 16px', whiteSpace: 'nowrap', verticalAlign: 'middle' }}>
        <div style={{ fontSize: 10, color: 'var(--vo-steel)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 }}>{t('scanner:findings.labelLastSeen')}</div>
        <div style={{ fontSize: 13, color: 'var(--vo-ink-2)' }}>{formatDate(item.updated_at || item.inserted_at)}</div>
      </td>
      <td style={{ padding: '10px 16px', whiteSpace: 'nowrap', verticalAlign: 'middle' }}>
        <div style={{ fontSize: 10, color: 'var(--vo-steel)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 }}>{t('scanner:findings.labelFailingFor')}</div>
        <div style={{ fontSize: 13, color: 'var(--vo-ink-2)' }}>{failingFor(item.updated_at, item.inserted_at)}</div>
      </td>
    </tr>
  );
};

// ── Check group row (expandable, lazy-loads resources) ─────────────────────────
const CheckGroup = ({ group, slug, severity, status, accountMap }) => {
  const [open, setOpen] = useState(false);

  const { data: resourcesData, isLoading: resourcesLoading } = useQuery(
    ['checkResources', slug, group.checkId, severity, status],
    () => getScannerFindingsByCheck(slug, group.checkId, severity, status),
    { enabled: open && !!group.checkId, staleTime: 60000 },
  );

  const resources = resourcesData?.data?.findings || [];

  return (
    <>
      <tr
        onClick={() => setOpen(v => !v)}
        style={{
          cursor: 'pointer',
          borderTop: '1px solid var(--vo-line)',
          backgroundColor: open ? 'var(--vo-capri-50)' : 'var(--vo-white)',
        }}
        onMouseEnter={e => { if (!open) e.currentTarget.style.backgroundColor = 'var(--vo-mist)'; }}
        onMouseLeave={e => { if (!open) e.currentTarget.style.backgroundColor = open ? 'var(--vo-capri-50)' : 'var(--vo-white)'; }}
      >
        {/* Status */}
        <td style={{ padding: '14px 16px', whiteSpace: 'nowrap', verticalAlign: 'middle' }}>
          <StatusBadge status="FAIL" />
        </td>

        {/* Finding Groups: expand icon + check title */}
        <td style={{ padding: '14px 16px', verticalAlign: 'middle' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: 'var(--vo-slate)', flexShrink: 0, display: 'flex', alignItems: 'center' }}>
              {open
                ? <KeyboardArrowDown sx={{ fontSize: 18 }} />
                : <KeyboardArrowRight sx={{ fontSize: 18 }} />}
            </span>
            <div style={{ minWidth: 0 }}>
              <div style={{
                fontSize: 13, fontWeight: 600, color: 'var(--vo-ink)',
                lineHeight: 1.35, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {group.checkTitle}
              </div>
              {group.checkId && group.checkId !== group.checkTitle && (
                <div style={{ fontSize: 11, color: 'var(--vo-steel)', marginTop: 2 }}>
                  {group.checkId}
                </div>
              )}
            </div>
          </div>
        </td>

        {/* Severity */}
        <td style={{ padding: '14px 16px', whiteSpace: 'nowrap', verticalAlign: 'middle' }}>
          <SeverityBadge severity={group.severity} />
        </td>

        {/* Impacted Resources */}
        <td style={{ padding: '14px 16px', whiteSpace: 'nowrap', verticalAlign: 'middle' }}>
          <ResourcesBadge count={group.count} />
        </td>
      </tr>

      {/* Loading resources */}
      {open && resourcesLoading && (
        <tr style={{ borderTop: '1px solid var(--vo-line)', backgroundColor: 'var(--vo-mist)' }}>
          <td colSpan={7} style={{ padding: '16px', textAlign: 'center' }}>
            <CircularProgress size={20} />
          </td>
        </tr>
      )}

      {/* Resource sub-rows */}
      {open && !resourcesLoading && resources.map((item, idx) => (
        <ResourceRow
          key={item.id || item.uid || idx}
          item={item}
          accountMap={accountMap}
        />
      ))}

      {/* Blue bottom accent when open */}
      {open && !resourcesLoading && (
        <tr>
          <td colSpan={7} style={{ padding: 0, height: 3, backgroundColor: 'var(--vo-capri-100)' }} />
        </tr>
      )}
    </>
  );
};

// ── Main page ──────────────────────────────────────────────────────────────────
export const ScannerFindings = () => {
  const { t } = useTranslation('scanner');
  const [searchParams] = useSearchParams();
  const selectedTenant = useSelector(useAuthTenant);

  const [page, setPage] = useState(1);
  const [severity, setSeverity] = useState(searchParams.get('severity') || '');
  const [status, setStatus] = useState('FAIL');

  useEffect(() => { setPage(1); }, [severity, status]);
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, [page]);

  const { data, isLoading, isFetching } = useQuery(
    ['scannerFindingGroups', selectedTenant.slug, page, severity, status],
    () => getScannerFindingGroups(selectedTenant.slug, page, severity, status, 10),
    { enabled: !!selectedTenant.slug },
  );

  const { data: accountsData } = useQuery(
    ['scannerAccounts', selectedTenant.slug],
    () => getScannerAccounts(selectedTenant.slug),
    { enabled: !!selectedTenant.slug },
  );

  const groups = data?.data?.groups || [];
  const pagination = data?.data?.pagination || {};
  const totalPages = Number(pagination.pages) || 0;

  const accountMap = (accountsData?.data || []).reduce((acc, account) => {
    if (account.prowlerProviderId) acc[account.prowlerProviderId] = account.name;
    return acc;
  }, {});

  // '' means genuinely no selection; 'all' is the real, explicit "All" menu
  // choice — both mean "don't filter on this field," distinct only in how
  // the Select renders them (muted placeholder vs. full-strength value).
  const isFilterActive = (value) => value && value !== 'all';
  // status's own default is the explicit 'FAIL' value (not '' or 'all'), so
  // only a value that differs from FAIL *and* isn't the all-sentinel counts
  // as the user having changed it away from the default view.
  const isStatusChanged = status !== 'FAIL' && status !== 'all' && status !== '';

  return (
    <Container>
      <PageHeader
        title={t('scanner:findings.title')}
        segments={[
          { label: t('common:breadcrumbs.analysis') },
          { label: t('scanner:dashboard.title'), route: '/scanner/dashboard' },
          { label: t('scanner:findings.title') },
        ]}
      />
      <Box sx={{ mt: 3, pb: 6 }}>

      {/* Filters */}
      <Card elevation={0} sx={{ p: 2.5, mb: 3, border: '1px solid var(--vo-line)', borderRadius: 2 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <Select
            name="severity"
            value={severity}
            onChange={value => setSeverity(value)}
            fullWidth={false}
            sx={{ minWidth: 150 }}
            options={[
              { value: '', label: t('scanner:findings.selectSeverity') },
              { value: 'all', label: t('scanner:findings.all') },
              { value: 'critical', label: t('scanner:severity.critical') },
              { value: 'high', label: t('scanner:severity.high') },
              { value: 'medium', label: t('scanner:severity.medium') },
              { value: 'low', label: t('scanner:severity.low') },
              { value: 'informational', label: t('scanner:severity.informational_full') },
            ]}
          />

          <Select
            name="status"
            value={status}
            onChange={value => setStatus(value)}
            fullWidth={false}
            sx={{ minWidth: 130 }}
            options={[
              { value: '', label: t('scanner:findings.selectStatus') },
              { value: 'all', label: t('scanner:findings.allStatus') },
              { value: 'FAIL', label: t('scanner:findings.failed') },
              { value: 'PASS', label: t('scanner:findings.passed') },
              { value: 'MUTED', label: t('scanner:findings.muted') },
            ]}
          />

          {isFilterActive(severity) && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '4px 10px', borderRadius: 20,
              border: `1px solid ${SEVERITY_DOT[severity] || 'var(--vo-steel)'}`,
              backgroundColor: SEVERITY_BG[severity] || 'var(--vo-mist)',
              color: SEVERITY_DOT[severity] || 'var(--vo-slate)',
              fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap',
            }}>
              {t(`scanner:severity.${severity}`) || severity}
              <button
                onClick={() => setSeverity('')}
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 16, height: 16, borderRadius: '50%', border: 'none',
                  backgroundColor: 'transparent', cursor: 'pointer', padding: 0,
                  color: 'inherit', fontSize: 14, lineHeight: 1,
                }}
              >
                ×
              </button>
            </span>
          )}

          {(isFilterActive(severity) || isStatusChanged) && (
            <button
              onClick={() => { setSeverity(''); setStatus('FAIL'); }}
              style={{
                border: 'none', background: 'none', cursor: 'pointer',
                fontSize: 13, color: 'var(--vo-slate)', padding: '4px 8px',
                textDecoration: 'underline',
              }}
            >
              {t('scanner:findings.clearFilters')}
            </button>
          )}
        </div>
      </Card>

      {/* Groups table */}
      <Card elevation={0} sx={{ padding: 0, border: '1px solid var(--vo-line)', borderRadius: 2, overflow: 'hidden' }}>
        {isLoading && (
          <Box sx={{ p: 6, textAlign: 'center', color: 'var(--vo-slate)' }}>
            <CircularProgress size={28} sx={{ mb: 2 }} />
            <Typography sx={{ fontSize: 13 }}>{t('scanner:findings.loading')}</Typography>
          </Box>
        )}

        {!isLoading && groups.length === 0 && (
          <Box sx={{ p: 8, textAlign: 'center' }}>
            <Typography sx={{ color: 'var(--vo-slate)' }}>
              {t('scanner:findings.noFindings')}
            </Typography>
          </Box>
        )}

        {groups.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <colgroup>
                <col style={{ width: 90 }} />
                <col />
                <col style={{ width: 120 }} />
                <col style={{ width: 160 }} />
              </colgroup>
              <thead>
                <tr style={{ backgroundColor: 'var(--vo-mist)' }}>
                  {[
                    t('scanner:findings.colStatus'),
                    t('scanner:findings.colFindingGroups'),
                    t('scanner:findings.colSeverity'),
                    t('scanner:findings.colImpacted'),
                  ].map(col => (
                    <th key={col} style={{
                      padding: '11px 16px',
                      fontWeight: 600, fontSize: 11, color: 'var(--vo-slate)',
                      textAlign: 'left', textTransform: 'uppercase', letterSpacing: 0.5,
                      borderBottom: '1px solid var(--vo-line)', whiteSpace: 'nowrap',
                    }}>
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {groups.map(group => (
                  <CheckGroup
                    key={group.checkId || group.checkTitle}
                    group={group}
                    slug={selectedTenant.slug}
                    severity={severity}
                    status={status}
                    accountMap={accountMap}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <Stack sx={{ justifyContent: 'center', flexDirection: 'row', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, p) => {
              if (p !== page) {
                setPage(p);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
            color="primary"
            showFirstButton
            showLastButton
            disabled={isFetching}
          />
        </Stack>
      )}
      </Box>
    </Container>
  );
};
